// The words and the drawing sums of accounts by heat (HeatPage.tsx), kept pure so each is tested (heat-words.test.ts).
// Every string here is built from the read (GET /api/deals/heat); none is a placeholder (U152).
import { dayMonth } from '../day.js';
import type { ByKeenness, Found, HeatFigure, HeatMoney, HeatRing, HeatRow, HoldLine, Named, PromiseLine, StageStep } from './api.js';

const SYMBOL: Record<string, string> = { GBP: '£', EUR: '€', USD: '$' };

function prefixed(amount: string, currency: string | null): string {
  const sym = currency ? SYMBOL[currency] : undefined;
  return sym ? `${sym}${amount}` : currency ? `${amount} ${currency}` : amount;
}

/** "1437000.00" in GBP as "£1,437,000"; pence only where there are any. A string that is not an amount is shown as written. */
export function moneyFull(amount: string, currency: string | null): string {
  const n = Number(amount);
  if (!/^\d+(\.\d+)?$/.test(amount.trim()) || !Number.isFinite(n)) return amount;
  const shown = n.toLocaleString('en-GB', { minimumFractionDigits: n % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 });
  return prefixed(shown, currency);
}

/** A ring's middle: "£1.44m", "£650k", "£48k", "£900". Rounded for the eye; the full figure is on the line and the row. */
export function moneyShort(amount: string, currency: string | null): string {
  const n = Number(amount);
  if (!Number.isFinite(n)) return amount;
  if (n >= 1_000_000) return prefixed(`${(n / 1_000_000).toFixed(2).replace(/\.?0+$/, '')}m`, currency);
  if (n >= 10_000) return prefixed(`${Math.round(n / 1000)}k`, currency);
  if (n >= 1000) return prefixed(`${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`, currency);
  return prefixed(String(Math.round(n)), currency);
}

/** "Tom" from "Tom Johnson"; null for nobody or a name not on record. */
export function firstName(n: Named | null): string | null {
  const name = n?.name?.trim();
  return name ? name.split(/\s+/)[0]! : null;
}

/** "Tom's", "Chris's". */
export function possessive(name: string): string {
  return `${name}'s`;
}

export type Segment = { key: keyof ByKeenness; len: number; offset: number };

/**
 * A ring's segments, keenest first, clockwise from the top: each one's share of the ring is its share of the figures, a
 * small gap between two. `circumference` is the ring's own; an empty ring has no segment and draws only its track.
 */
export function ringSegments(k: ByKeenness, circumference: number, gap = 2): Segment[] {
  const order: Array<keyof ByKeenness> = ['k5', 'k4', 'k3', 'k2', 'k1', 'none'];
  const parts = order.map((key) => ({ key, v: Number(k[key]) })).filter((p) => Number.isFinite(p.v) && p.v > 0);
  const total = parts.reduce((s, p) => s + p.v, 0);
  if (total <= 0) return [];
  const g = parts.length > 1 ? gap : 0;
  let at = 0;
  return parts.map((p) => {
    const share = (p.v / total) * circumference;
    const seg = { key: p.key, len: Math.max(share - g, 0.5), offset: at };
    at += share;
    return seg;
  });
}

/** "[n] of [m] with a figure", or what was read when there is none. */
export function withFigureWords(m: HeatMoney): string {
  return `${m.withFigure} of ${m.of} with a figure`;
}

/** An opportunity's year figure in words (U220): whose it is or where it came from, and when. */
export function figureWords(f: HeatFigure | null): string {
  if (!f) return 'No figure yet';
  const amount = `${moneyFull(f.amount, f.currency)} a year`;
  const who = f.setBy === 'person' && f.by ? (f.by.mine ? 'your figure' : `${possessive(firstName(f.by) ?? 'A colleague')} figure`) : (f.source ?? 'from the sheet');
  return `${amount}, ${who.charAt(0).toLowerCase()}${who.slice(1)}, ${dayMonth(new Date(f.at))}`;
}

/** A client's promise's date: "by 13 Oct", "w/c 5 Oct", or nothing; past it, the days counted (D68). */
export function dueWords(p: Pick<PromiseLine, 'dueAt' | 'precision' | 'daysPast'>): { words: string; late: boolean } {
  if (p.daysPast !== null && p.daysPast > 0) return { words: `${p.daysPast} ${p.daysPast === 1 ? 'day' : 'days'} past`, late: true };
  if (!p.dueAt) return { words: '', late: false };
  const d = dayMonth(new Date(p.dueAt), 'utc');
  return { words: p.precision === 'week' ? `w/c ${d}` : `by ${d}`, late: false };
}

/** Whose decision a hold is (U208), and since when. */
export function holdWhoWords(h: HoldLine): string {
  const n = h.owner?.mine ? 'Your' : firstName(h.owner) ? possessive(firstName(h.owner)!) : null;
  return `${n ? `${n} decision` : 'Nobody’s decision yet'}, since ${dayMonth(new Date(h.since))}`;
}

/** Who waits on a client's promise: the reader, or a colleague. */
export function waitingWords(p: PromiseLine): string {
  const who = p.waiting?.mine ? 'Yours' : firstName(p.waiting) ? possessive(firstName(p.waiting)!) : 'Nobody’s';
  return `${who}, since ${dayMonth(new Date(p.since))}`;
}

/** Does a line match the find? Account, company, product, owner or the account's owner, case and accent aside. */
export function lineMatches(r: HeatRow, q: string): boolean {
  const norm = (x: string) => x.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
  const term = norm(q.trim());
  if (!term) return true;
  const hay = [r.accountName ?? '', r.organisationName ?? '', r.product ?? '', r.title, r.holder?.name ?? '', r.accountOwner?.name ?? ''];
  return hay.some((x) => norm(x).includes(term));
}

/** The drop-downs over the list (the search plan's Deals row, U228; By stage, the live card brief), kept in the address
 *  as ?product= and the rest. By band went with the bands (U290; Mike, by pop-up on 5 Oct 2026: "Drop it"). Mine is
 *  ?owner=<the reader> (U283). */
export interface HeatFilter { product: string; owner: string; keenness: string; stage: string }
export interface FilterOption { value: string; label: string; n: number }

/** A keenness as its drop-down's value: "5", "4", "3", "12" for 1 or 2, "none" for no reaction yet. */
export function keennessKey(k: number | null): string {
  return k === null ? 'none' : k >= 3 ? String(k) : '12';
}

/**
 * Is a line in what the drop-downs are set to? `product` is a ring's family, matched through the ring's own
 * opportunities; `owner` is who owns the opportunity, or "none". `skip` leaves one drop-down out: the rings follow
 * every filter but the product, so a pressed ring leaves the others showing what pressing them would give (Mike, by
 * pop-up on 5 Oct 2026: "Others keep their figures", U291).
 */
export function lineInFilter(r: HeatRow, f: HeatFilter, ringDeals: ReadonlySet<string> | null, skip?: keyof HeatFilter): boolean {
  if (skip !== 'product' && f.product && !ringDeals?.has(r.id)) return false;
  if (skip !== 'owner' && f.owner && (f.owner === 'none' ? r.holder !== null : r.holder?.principalId !== f.owner)) return false;
  if (skip !== 'keenness' && f.keenness && keennessKey(r.keenness) !== f.keenness) return false;
  if (skip !== 'stage' && f.stage && r.live?.stageCode !== f.stage) return false;
  return true;
}

const KEENNESS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '5', label: 'Keenness 5' }, { value: '4', label: 'Keenness 4' }, { value: '3', label: 'Keenness 3' },
  { value: '12', label: 'Keenness 1 or 2' }, { value: 'none', label: 'No reaction yet' },
];

/**
 * Every option of the drop-downs and how many lines each holds, from the whole read (U152), never the lines on screen.
 * With `f` set, each drop-down's counts are of the lines every other drop-down leaves, so an option's count is what
 * pressing it shows (BUGS.md D201: with Premier Padel picked, "Proposal sent (18)" counted every product while the list
 * showed 5). An option nothing leaves a line at is dropped, but the one picked stays, so the drop-down still says it.
 */
export function filterOptions(all: readonly HeatRow[], rings: ReadonlyArray<{ family: string; dealIds: readonly string[] }>, stages: readonly StageStep[] = [], f?: HeatFilter): Record<keyof HeatFilter, FilterOption[]> {
  const picked = f ? rings.find((r) => r.family === f.product) : undefined;
  const ringDeals = picked ? new Set(picked.dealIds) : null;
  const left = (skip: keyof HeatFilter) => (f ? all.filter((l) => lineInFilter(l, f, ringDeals, skip)) : all);
  const kept = (skip: keyof HeatFilter) => (o: FilterOption) => o.n > 0 || (f !== undefined && f[skip] === o.value);

  const forProduct = left('product');
  const product = rings.map((r) => {
    const ids = new Set(r.dealIds);
    return { value: r.family, label: r.family, n: forProduct.filter((l) => ids.has(l.id)).length };
  }).filter(kept('product'));
  // Owners are named from the whole read, so the one picked keeps its name when no other choice leaves a line of theirs.
  const owners = new Map<string, FilterOption>();
  for (const l of all) {
    const key = l.holder?.principalId ?? 'none';
    if (owners.has(key)) continue;
    owners.set(key, { value: key, label: l.holder ? (l.holder.mine ? 'Yours' : l.holder.name ?? 'A colleague') : 'No owner', n: 0 });
  }
  for (const l of left('owner')) owners.get(l.holder?.principalId ?? 'none')!.n += 1;
  const owner = [...owners.values()].filter(kept('owner')).sort((a, b) => Number(b.label === 'Yours') - Number(a.label === 'Yours') || Number(a.value === 'none') - Number(b.value === 'none') || a.label.localeCompare(b.label));
  const forKeenness = left('keenness');
  const keenness = KEENNESS_OPTIONS.map((k) => ({ ...k, n: forKeenness.filter((l) => keennessKey(l.keenness) === k.value).length })).filter(kept('keenness'));
  const forStage = left('stage');
  const stage = stages.map((x) => ({ value: x.code, label: x.name, n: forStage.filter((l) => l.live?.stageCode === x.code).length })).filter(kept('stage'));
  return { product, owner, keenness, stage };
}

// ---------------------------------------------------------------------------------------------------------------------
// The rings and the waiting lines add up the lines in view (U280; the brief, section 3). The sums are the API's own
// (heat-shape.ts money and ringOf), in hundredths so they are exact, over the lines the filters leave.

/** "1389000.5" as 138900050n; a string that is not a plain decimal adds nothing (the API refuses one before it gets here). */
function cents(amount: string): bigint | null {
  const m = /^(\d+)(?:\.(\d{1,2}))?$/.exec(amount.trim());
  return m ? BigInt(m[1]!) * 100n + BigInt((m[2] ?? '').padEnd(2, '0')) : null;
}
function fromCents(c: bigint): string {
  const x = c.toString().padStart(3, '0');
  return `${x.slice(0, -2)}.${x.slice(-2)}`;
}

/** The year's figures of these lines: the home currency added, others apart and never converted (D300 part 8). */
export function moneyOf(rows: readonly HeatRow[], home: string | null): HeatMoney {
  let homeCents = 0n;
  let withFigure = 0;
  const other = new Map<string, { c: bigint; n: number }>();
  for (const r of rows) {
    if (!r.figure) continue;
    withFigure++;
    const c = cents(r.figure.amount) ?? 0n;
    if (home !== null && r.figure.currency === home) homeCents += c;
    else { const x = other.get(r.figure.currency) ?? { c: 0n, n: 0 }; other.set(r.figure.currency, { c: x.c + c, n: x.n + 1 }); }
  }
  return {
    home: fromCents(homeCents), withFigure, of: rows.length,
    other: [...other].sort((a, b) => a[0].localeCompare(b[0])).map(([currency, x]) => ({ currency, amount: fromCents(x.c), count: x.n })),
  };
}

/** A ring over these lines: the home sum split by keenness, 5 to 1 each its own and none (U219, U269). */
export function ringOfRows(rows: readonly HeatRow[], home: string | null): HeatRing {
  const seg = { k5: 0n, k4: 0n, k3: 0n, k2: 0n, k1: 0n, none: 0n };
  for (const r of rows) {
    if (!r.figure || home === null || r.figure.currency !== home) continue;
    const c = cents(r.figure.amount) ?? 0n;
    const k = r.keenness;
    if (k === null) seg.none += c; else if (k >= 5) seg.k5 += c; else if (k === 4) seg.k4 += c; else if (k === 3) seg.k3 += c; else if (k === 2) seg.k2 += c; else seg.k1 += c;
  }
  return {
    ...moneyOf(rows, home),
    byKeenness: { k5: fromCents(seg.k5), k4: fromCents(seg.k4), k3: fromCents(seg.k3), k2: fromCents(seg.k2), k1: fromCents(seg.k1), none: fromCents(seg.none) },
  };
}

/** Each product's ring over the lines in view; which opportunities a ring holds is the API's (ring.dealIds). */
export function ringsInView<R extends { family: string; dealIds: string[] }>(rings: readonly R[], inView: readonly HeatRow[], home: string | null): Array<HeatRing & { family: string; dealIds: string[] }> {
  const byId = new Map(inView.map((r) => [r.id, r]));
  return rings.map((ring) => ({ family: ring.family, dealIds: ring.dealIds, ...ringOfRows(ring.dealIds.flatMap((id) => byId.get(id) ?? []), home) }));
}

/**
 * The line under the rings saying whose they are (the brief, section 3): "Will's revenue potential, 14 of 22 with a
 * figure", "Your revenue potential, ...", the team's while nobody's are picked out.
 */
export function whoseRingWords(owner: string, ownerNamed: Named | null, m: HeatMoney): string {
  const whose = owner === '' ? possessive('The team')
    : ownerNamed?.mine ? 'Your'
      : owner === 'none' ? null
        : possessive(firstName(ownerNamed) ?? 'A colleague');
  return `${whose ? `${whose} revenue potential` : 'Revenue potential with no owner'}, ${m.withFigure} of ${m.of} with a figure`;
}

/**
 * A decision holding opportunities, narrowed to the lines in view: its counts, ring and proposed minimums over those
 * lines, or nothing when none of them is keen (a line of Waiting on us is a decision holding keen ones, U208). With
 * nothing narrowing, it is the API's line as read.
 */
export function holdInView(h: HoldLine, inView: ReadonlyMap<string, HeatRow>, home: string | null): HoldLine | null {
  const rows = h.dealIds.flatMap((id) => inView.get(id) ?? []);
  const keen = rows.filter((r) => (r.keenness ?? 0) >= 4);
  if (keen.length === 0) return null;
  const sheet = rows.filter((r) => r.figure?.setBy === 'sheet');
  return { ...h, holds: rows.length, keen: keen.length, ring: ringOfRows(rows, home), proposed: sheet.length ? moneyOf(sheet, home) : null, dealIds: rows.map((r) => r.id) };
}

/** A client's promise is in view while its opportunity's line is. */
export const promiseInView = (p: PromiseLine, inView: ReadonlyMap<string, HeatRow>): boolean => inView.has(p.dealId);

/** What Find prints beside a deal: its company, and "Archived" for an archived one, as the top bar's search does (BUGS.md
 *  D199, U391). */
export function foundDealWhat(d: Found['deals'][number]): string {
  return [d.organisationName, d.archived ? 'Archived' : null].filter((x): x is string => typeof x === 'string' && x !== '').join(' · ');
}
